import { Injectable, OnDestroy } from '@angular/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { fromEvent, Observable, Subject } from 'rxjs';
import { map, filter, debounceTime, takeUntil } from 'rxjs/operators';
import { XSMALL, SMALL, MEDIUM, LARGE, XLARGE, XXLARGE } from '@config/breakpoints.config';
import { ScreenSize } from '@core/enums/screen-size.enum';
import { Browser, SsrService } from '@interticket/core';

@Injectable()
export class CoreService implements OnDestroy {

  private readonly destroy$ = new Subject<void>();

  constructor(
    private breakpointObserver: BreakpointObserver,
    private ssrService: SsrService
  ) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @Browser()
  setViewHeight(): void {
    const currentHeight = this.ssrService.window.innerHeight;
    this.ssrService.document.documentElement.style.setProperty('--viewport-height', currentHeight + 'px');
  }

  /**
   * The trick to viewport units on mobile
   * https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
   *
   * Set the initial view height (vh) and keep it up to date
   * Needed for proper mobile vh calculation
   */
  @Browser()
  subscribeToResize(): void {
    fromEvent(this.ssrService.window, 'resize')
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(50)
      )
      .subscribe(() => this.setViewHeight());
  }

  getScreenSize(): Observable<ScreenSize> {
    return this.breakpointObserver
      .observe([XSMALL, SMALL, MEDIUM, LARGE, XLARGE, XXLARGE])
      .pipe(
        filter((state: BreakpointState) => !!state.matches),
        map((state: BreakpointState) => {
          if (state.breakpoints[XSMALL]) {
            return ScreenSize.XSMALL;
          }
          if (state.breakpoints[SMALL]) {
            return ScreenSize.SMALL;
          }
          if (state.breakpoints[MEDIUM]) {
            return ScreenSize.MEDIUM;
          }
          if (state.breakpoints[LARGE]) {
            return ScreenSize.LARGE;
          }
          if (state.breakpoints[XLARGE]) {
            return ScreenSize.XLARGE;
          }
          if (state.breakpoints[XXLARGE]) {
            return ScreenSize.XXLARGE;
          }
        })
      );
  }

}
